import React, { useState } from 'react';
import { Check, Star } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import BookingPopup from '@/components/BookingPopup';
import { linkedinOutreachTiers } from '@/config/linkedinOutreachPricing';

interface Props {
  compact?: boolean;
  showHeader?: boolean;
}

const LinkedInOutreachPricing: React.FC<Props> = ({ compact = false, showHeader = true }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#555555';
  const borderColor = isDark ? '#333333' : '#E5E5E5';
  const accentColor = '#0d7680'; // teal (brand)
  const popularBorder = isDark ? '#FFFFFF' : '#111111';

  return (
    <section className={`relative ${compact ? 'py-12' : 'py-16 lg:py-24'}`}>
      <div className="max-w-[1100px] mx-auto px-[7%] lg:px-8">
        {showHeader && (
          <div className="text-center mb-12">
            <h2
              className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl lg:text-5xl'} font-bold mb-3`}
              style={{ color: headingColor }}
            >
              Pricing that rewards commitment.
            </h2>
            <p className="text-sm max-w-2xl mx-auto" style={{ color: textColor }}>
              Three ways to deploy AI LinkedIn outreach. The 90-Day Pilot gives the algorithm
              the runway it needs to actually book meetings — and waives your setup fee.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {linkedinOutreachTiers.map((tier) => {
            const isPopular = tier.popular;
            return (
              <div
                key={tier.id}
                className="relative rounded-2xl p-6 flex flex-col transition-all duration-200 hover:scale-[1.02]"
                style={{
                  backgroundColor: cardBg,
                  border: isPopular ? `2px solid ${popularBorder}` : `1px solid ${borderColor}`,
                  boxShadow: isPopular
                    ? isDark
                      ? '0 20px 40px -10px rgba(0,0,0,0.5)'
                      : '0 20px 40px -10px rgba(0,0,0,0.12)'
                    : 'none',
                }}
              >
                {tier.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"
                    style={{ backgroundColor: popularBorder, color: isDark ? '#111111' : '#FFFFFF' }}
                  >
                    <Star className="w-3 h-3" fill="currentColor" />
                    {tier.badge}
                  </div>
                )}

                <div className="mb-4">
                  <h3 className="text-base font-semibold mb-1" style={{ color: headingColor }}>
                    {tier.name}
                  </h3>
                  <p className="text-xs" style={{ color: textColor }}>{tier.tagline}</p>
                </div>

                <div className="mb-3">
                  <span className="text-4xl font-bold" style={{ color: headingColor }}>
                    {tier.monthlyPrice}
                  </span>
                  <span className="text-sm" style={{ color: textColor }}>
                    {tier.monthlySuffix}
                  </span>
                </div>

                {/* Setup fee — crossed-out anchor for tiers 2 & 3 */}
                <div className="mb-4 text-xs flex items-center gap-2" style={{ color: textColor }}>
                  {tier.setupWaived ? (
                    <>
                      <span className="line-through opacity-60">{tier.setupFee}</span>
                      <span
                        className="font-semibold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: accentColor, color: '#FFFFFF' }}
                      >
                        Waived
                      </span>
                    </>
                  ) : (
                    <span>+ {tier.setupFee} (one-off)</span>
                  )}
                </div>

                <div
                  className="rounded-lg p-3 mb-5"
                  style={{ backgroundColor: isDark ? '#0F0F0F' : '#F7F6F4', border: `1px solid ${borderColor}` }}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-wider" style={{ color: textColor }}>
                      {tier.dayOneLabel}
                    </span>
                    <span className="text-sm font-semibold" style={{ color: headingColor }}>
                      {tier.dayOneAmount}
                    </span>
                  </div>
                  <p className="text-[11px] mt-1" style={{ color: textColor }}>
                    {tier.commitment}
                  </p>
                </div>

                {!compact && (
                  <p className="text-xs mb-5 leading-relaxed" style={{ color: textColor }}>
                    {tier.description}
                  </p>
                )}

                <ul className="space-y-2 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                        style={{ color: accentColor }}
                        strokeWidth={2.5}
                      />
                      <span className="text-xs" style={{ color: textColor }}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setIsBookingOpen(true)}
                  className="w-full px-4 py-2.5 text-xs font-semibold rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: isPopular ? popularBorder : 'transparent',
                    color: isPopular ? (isDark ? '#111111' : '#FFFFFF') : headingColor,
                    border: isPopular ? 'none' : `1px solid ${borderColor}`,
                  }}
                >
                  {tier.ctaLabel}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <BookingPopup isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </section>
  );
};

export default LinkedInOutreachPricing;
